import { getLocationCoordinates } from './DeviceMapUtils';
import { calculateDistanceInMiles } from 'utils/maps/geometry.utils';

/**
 * floodChain.utils — a flood wave travelling down a chain of Stage devices,
 * read off the devices' own data.
 *
 * Mike, 2026-08-28 (an experiment): "if more than 2 nodes are connected we
 * could do some time extrapolation that could allow us to calculate the
 * theoretical flow rate and show that on the map, highlight the path".
 *
 * THE INPUTS are what the device list already carries:
 *   - `device_settings.sim.routing`  who is upstream — the links make the
 *                                    chain — and the hop's straight-line miles
 *   - `device_settings.depthDet1`    threshold 1 — "the water is here"
 *   - `data[]`                       the day's datapoints, `ts` + `depth1`
 *
 * THE MODEL, per chain, head to tail:
 *   arrival(gauge)     the LAST rising crossing of threshold 1 in its data —
 *                      the moment the wave reached it
 *   observed mph(hop)  hop miles ÷ (arrival(to) − arrival(from)), when both
 *                      have arrived
 *   flow rate(chain)   the distance-weighted mean of the observed hops; with
 *                      two or more observed hops the change between the first
 *                      and last hop is the trend ("slowing", "speeding up")
 *   ETA(gauge)         the last arrival on the chain + the remaining miles at
 *                      the observed rate — or, before anything has arrived, at
 *                      the configured flow speed
 *   wave position      a point on the next hop, the fraction of its expected
 *                      travel time that has elapsed
 *   THE THEORY         where the wave would be NOW if it left the head at the
 *                      head's arrival and moved at the configured speeds — the
 *                      plan the simulator runs (chainWalk anchors every routed
 *                      device to the head's clock minus its summed travel time).
 *                      Drawn beside the observed wave: apart, the water is not
 *                      moving as configured.
 *
 * Straight-line miles, as the routing card measured them. Pure: no store, no
 * network, the clock is an argument.
 */

export type LatLng = [number, number];

export interface ChainUpstreamLink {
  uid: number;
  travel_minutes?: number;
  distance_miles?: number;
  flow_speed_mph?: number;
}

export interface ChainRouting {
  chain?: string;
  position?: number;
  upstream_uid?: number | null;
  /** Several when tributaries join here; the links are the truth. */
  upstreams?: ChainUpstreamLink[];
  lag_minutes?: number;
  distance_miles?: number;
  travel_minutes?: number;
  flow_speed_mph?: number;
}

export interface ChainDevice {
  id: number;
  name?: string;
  alias?: string;
  device_type?: string;
  location?: unknown;
  reporting_mode?: string | null;
  device_settings?: {
    depthDet1?: number | string | null;
    depthDet2?: number | string | null;
    sim?: { routing?: ChainRouting | null } | null;
  } | null;
  data?: Array<{ ts?: number | string; depth1?: number | string | null }> | null;
}

// ── Node conditions: the colour of the line is the colour of the pins ──────

/**
 * The pin's own rule (icon.utils `stageNodeStatus`), read off the LATEST
 * datapoint: at or above threshold 2 the gauge is flooding (red), at or above
 * threshold 1 it is warning (yellow), otherwise normal (green); no data, or
 * reporting Offline, is grey. Mike, 2026-08-28: "change the color of the
 * sections of the line to be the same as the conditions of the nodes".
 */
export type NodeCondition = 'flooding' | 'warning' | 'normal' | 'offline';

export const CONDITION_COLOR: Record<NodeCondition, string> = {
  flooding: '#e53935',
  warning: '#fdd835',
  normal: '#43a047',
  offline: '#9e9e9e',
};

export function nodeCondition(device: ChainDevice | null | undefined): NodeCondition {
  if (!device || device.reporting_mode === 'Offline') return 'offline';
  const points = Array.isArray(device.data) ? device.data : [];
  let latest: { ts: number; depth: number } | null = null;
  for (const p of points) {
    const ts = num(p?.ts);
    const depth = num(p?.depth1);
    if (ts == null || depth == null) continue;
    if (!latest || ts > latest.ts) latest = { ts, depth };
  }
  if (!latest) return 'offline';
  const det2 = num(device.device_settings?.depthDet2);
  const det1 = num(device.device_settings?.depthDet1);
  if (det2 != null && det2 > 0 && latest.depth >= det2) return 'flooding';
  if (det1 != null && det1 > 0 && latest.depth >= det1) return 'warning';
  return 'normal';
}

/** '#rrggbb' mixed `t` of the way from `a` to `b`. */
export function mixHex(a: string, b: string, t: number): string {
  const f = Math.min(1, Math.max(0, t));
  const ca = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const cb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `#${ca.map((v, i) => Math.round(v + (cb[i] - v) * f).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * A hop drawn as `steps` short segments whose colour runs from the upstream
 * node's condition to the downstream node's — Leaflet paints one colour per
 * polyline, so a gradient is several polylines.
 */
export function gradientSegments(
  a: LatLng,
  b: LatLng,
  fromColor: string,
  toColor: string,
  steps = 6,
): Array<{ positions: [LatLng, LatLng]; color: string }> {
  const n = Math.max(1, Math.floor(steps));
  const out: Array<{ positions: [LatLng, LatLng]; color: string }> = [];
  for (let i = 0; i < n; i++) {
    out.push({
      positions: [interpolate(a, b, i / n), interpolate(a, b, (i + 1) / n)],
      color: mixHex(fromColor, toColor, (i + 0.5) / n),
    });
  }
  return out;
}

/** Compass bearing from `a` to `b` in degrees, 0 = north, 90 = east (planar, fine at hop scale). */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const dLat = b[0] - a[0];
  const dLng = (b[1] - a[1]) * Math.cos(((a[0] + b[0]) / 2) * (Math.PI / 180));
  return (Math.atan2(dLng, dLat) * (180 / Math.PI) + 360) % 360;
}

export type HopStatus = 'passed' | 'next' | 'pending' | 'unknown';

export interface ChainHop {
  from: ChainDevice;
  to: ChainDevice;
  fromCoords: LatLng | null;
  toCoords: LatLng | null;
  distanceMiles: number | null;
  /** The card's configured travel time for the hop, if it stored one. */
  configuredMinutes: number | null;
  fromArrivalTs: number | null;
  toArrivalTs: number | null;
  /** miles ÷ hours between the two arrivals; null until both have arrived. */
  observedMph: number | null;
  /** When the wave is expected at `to`; null when it cannot be told. */
  etaTs: number | null;
  /** When the wave is due at `to` on the plan: the head's arrival plus the configured minutes down to it. */
  theoryEtaTs: number | null;
  status: HopStatus;
}

export interface ChainTheory {
  /** Per device, head first: when the wave is due there on the plan. Null past a hop with no configured time. */
  arrivals: Array<number | null>;
  /** Where the wave would be now on the plan; null before the head's arrival is known. */
  wave: { hop: ChainHop; position: LatLng; fraction: number } | null;
  /** True once the plan has the wave past the last gauge. */
  atTail: boolean;
  /** The last observed arrival minus its planned time, in minutes: > 0 late against the plan, < 0 early. */
  lastArrivalVsPlanMin: number | null;
}

export interface ChainAnalysis {
  chain: string;
  devices: ChainDevice[];
  hops: ChainHop[];
  /** Distance-weighted mean of the observed hops; null with none observed. */
  observedMph: number | null;
  hopsObserved: number;
  /** Observed speed on the last hop minus on the first — with ≥ 2 observed hops. */
  trendMph: number | null;
  /** The speed the ETAs use: observed when there is one, else the configured one. */
  etaMph: number | null;
  etaBasis: 'observed' | 'configured' | 'none';
  /** The most downstream gauge the wave has reached, if any. */
  lastArrival: { device: ChainDevice; ts: number } | null;
  /** The wave's estimated position now, on the next hop. */
  wave: { hop: ChainHop; position: LatLng; fraction: number } | null;
  /** The plan — the wave at the configured speeds from the head's arrival. */
  theory: ChainTheory | null;
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** The JS coordinate helper returns a plain array; the map wants a tuple. */
const toLatLng = (coords: number[] | null | undefined): LatLng | null =>
  Array.isArray(coords) && coords.length >= 2 ? [coords[0], coords[1]] : null;

export const routingOf = (device: ChainDevice | null | undefined): ChainRouting | null =>
  device?.device_settings?.sim?.routing ?? null;

export const deviceLabel = (device: ChainDevice | null | undefined): string =>
  device?.alias || device?.name || (device?.id != null ? `device ${device.id}` : 'device');

/**
 * The last rising crossing of threshold 1 in the device's datapoints — the
 * moment the wave reached it — or null when it never crossed, has no
 * threshold, or has no data. Datapoints arrive newest first; scanned oldest
 * first so "rising" means what it says.
 */
export function lastRisingCrossingTs(device: ChainDevice | null | undefined): number | null {
  const threshold = num(device?.device_settings?.depthDet1);
  const points = Array.isArray(device?.data) ? device!.data! : [];
  if (threshold == null || points.length === 0) return null;

  const ordered = points
    .map((p) => ({ ts: num(p?.ts), depth: num(p?.depth1) }))
    .filter((p): p is { ts: number; depth: number } => p.ts != null && p.depth != null)
    .sort((a, b) => a.ts - b.ts);

  let above = false;
  let crossing: number | null = null;
  for (const p of ordered) {
    const nowAbove = p.depth >= threshold;
    if (nowAbove && !above) crossing = p.ts;
    above = nowAbove;
  }
  return crossing;
}

/** The devices linked directly below `device`. */
/**
 * The upstream links of a device: `upstreams[]` when the row has them, else the
 * single `upstream_uid` with the hop figures stored beside it (an older row).
 */
export function upstreamLinksOf(device: ChainDevice | null | undefined): Array<{ uid: number; travelMinutes: number | null; distanceMiles: number | null; flowSpeedMph: number | null }> {
  const r = routingOf(device);
  if (!r) return [];
  if (Array.isArray(r.upstreams)) {
    return r.upstreams
      .filter((l) => l && Number.isInteger(l.uid))
      .map((l) => ({ uid: l.uid, travelMinutes: num(l.travel_minutes), distanceMiles: num(l.distance_miles), flowSpeedMph: num(l.flow_speed_mph) }));
  }
  if (r.upstream_uid == null) return [];
  return [{ uid: r.upstream_uid, travelMinutes: num(r.travel_minutes), distanceMiles: num(r.distance_miles), flowSpeedMph: num(r.flow_speed_mph) }];
}

/** The link from `from` into `to`, if `to` lists it. */
const linkBetween = (from: ChainDevice, to: ChainDevice) => upstreamLinksOf(to).find((l) => l.uid === from.id) ?? null;

export const downstreamOf = (device: ChainDevice, devices: ChainDevice[]): ChainDevice[] =>
  devices.filter((d) => d.id !== device.id && upstreamLinksOf(d).some((l) => l.uid === device.id));

/**
 * Every path from `device` up its links to a head — each head first. A
 * confluence gives one path per tributary. A link the list cannot resolve,
 * or a loop, ends that path where it is. Bounded: 32 hops, 64 paths.
 */
export function walkToHeads(device: ChainDevice, byId: Map<number, ChainDevice>): ChainDevice[][] {
  const out: ChainDevice[][] = [];
  const stack: Array<{ path: ChainDevice[]; seen: Set<number> }> = [{ path: [device], seen: new Set([device.id]) }];
  while (stack.length > 0 && out.length < 64) {
    const { path, seen } = stack.pop() as { path: ChainDevice[]; seen: Set<number> };
    const current = path[0];
    const links = upstreamLinksOf(current);
    if (links.length === 0 || path.length >= 32) {
      out.push(path);
      continue;
    }
    for (const link of [...links].reverse()) {
      const up = byId.get(link.uid);
      if (!up || seen.has(up.id)) {
        out.push(path);
        continue;
      }
      stack.push({ path: [up, ...path], seen: new Set([...seen, up.id]) });
    }
  }
  return out;
}

/** The first path to a head — what callers that know one chain expect. */
export function walkToHead(device: ChainDevice, byId: Map<number, ChainDevice>): ChainDevice[] {
  return walkToHeads(device, byId)[0] ?? [device];
}

/**
 * The chains among the devices, head first, read off the LINKS — not the
 * stored `chain` names, which the card once wrote per hop (six DEV gauges each
 * on a "chain-<upstream>" of its own). One chain per leaf — a linked device
 * with nothing below it — walked up to its head; a fork therefore yields one
 * chain per branch, sharing the hops above the fork. Named after the head:
 * what it stores, or `chain-<id>`, with the leaf added when two branches
 * share a head.
 */
export function buildChains(devices: ChainDevice[]): Array<{ chain: string; devices: ChainDevice[] }> {
  const byId = new Map(devices.map((d) => [d.id, d]));
  const paths = devices
    .filter((d) => upstreamLinksOf(d).length > 0 && downstreamOf(d, devices).length === 0)
    .flatMap((leaf) => walkToHeads(leaf, byId))
    .filter((path) => path.length >= 2);
  const headName = (path: ChainDevice[]) => routingOf(path[0])?.chain || `chain-${path[0].id}`;
  const shared = new Map<string, number>();
  paths.forEach((path) => shared.set(headName(path), (shared.get(headName(path)) ?? 0) + 1));
  return paths
    .map((path) => {
      const name = headName(path);
      const chain = (shared.get(name) ?? 0) > 1 ? `${name} → ${deviceLabel(path[path.length - 1])}` : name;
      return { chain, devices: path };
    })
    .sort((a, b) => a.chain.localeCompare(b.chain));
}

const hopMiles = (from: ChainDevice, to: ChainDevice): number | null => {
  const stored = linkBetween(from, to)?.distanceMiles ?? null;
  if (stored != null) return stored;
  const a = getLocationCoordinates(from.location);
  const b = getLocationCoordinates(to.location);
  return a && b ? calculateDistanceInMiles(a[0], a[1], b[0], b[1]) : null;
};

/** A point `fraction` of the way from a to b along the straight line. */
export function interpolate(a: LatLng, b: LatLng, fraction: number): LatLng {
  const f = Math.min(1, Math.max(0, fraction));
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

/** The whole picture for one chain, at `nowMs`. */
export function analyzeChain(chain: string, devices: ChainDevice[], nowMs: number): ChainAnalysis {
  const arrivals = devices.map((d) => lastRisingCrossingTs(d));

  // Observed speeds on hops whose both ends have arrived, in order.
  const hops: ChainHop[] = [];
  for (let i = 0; i < devices.length - 1; i++) {
    const from = devices[i];
    const to = devices[i + 1];
    const distanceMiles = hopMiles(from, to);
    const fromArrivalTs = arrivals[i];
    const toArrivalTs = arrivals[i + 1];
    let observedMph: number | null = null;
    if (distanceMiles != null && fromArrivalTs != null && toArrivalTs != null && toArrivalTs > fromArrivalTs) {
      observedMph = distanceMiles / ((toArrivalTs - fromArrivalTs) / 3_600_000);
    }
    hops.push({
      from,
      to,
      fromCoords: toLatLng(getLocationCoordinates(from.location)),
      toCoords: toLatLng(getLocationCoordinates(to.location)),
      distanceMiles,
      configuredMinutes: linkBetween(from, to)?.travelMinutes ?? null,
      fromArrivalTs,
      toArrivalTs,
      observedMph,
      etaTs: null,
      theoryEtaTs: null,
      status: 'unknown',
    });
  }

  const observed = hops.filter((h) => h.observedMph != null && h.distanceMiles != null);
  const totalMiles = observed.reduce((s, h) => s + (h.distanceMiles as number), 0);
  const observedMph =
    observed.length > 0 && totalMiles > 0
      ? observed.reduce((s, h) => s + (h.observedMph as number) * (h.distanceMiles as number), 0) / totalMiles
      : null;
  const trendMph =
    observed.length >= 2
      ? (observed[observed.length - 1].observedMph as number) - (observed[0].observedMph as number)
      : null;

  // The speed the extrapolation runs at.
  const configuredMph = devices.flatMap((d) => upstreamLinksOf(d).map((l) => l.flowSpeedMph)).find((v) => v != null && v > 0) ?? null;
  const etaMph = observedMph ?? configuredMph;
  const etaBasis: ChainAnalysis['etaBasis'] = observedMph != null ? 'observed' : configuredMph != null ? 'configured' : 'none';

  // The most downstream arrival: everything before it has passed.
  let lastIndex = -1;
  arrivals.forEach((ts, i) => {
    if (ts != null) lastIndex = i;
  });
  const lastArrival = lastIndex >= 0 ? { device: devices[lastIndex], ts: arrivals[lastIndex] as number } : null;

  // Statuses and ETAs downstream of the last arrival.
  let clockTs = lastArrival?.ts ?? null;
  hops.forEach((hop, i) => {
    if (i < lastIndex) {
      hop.status = 'passed';
      hop.etaTs = hop.toArrivalTs;
      return;
    }
    if (clockTs == null || etaMph == null || hop.distanceMiles == null) {
      hop.status = i === lastIndex ? 'next' : 'pending';
      return;
    }
    clockTs += (hop.distanceMiles / etaMph) * 3_600_000;
    hop.etaTs = clockTs;
    hop.status = i === lastIndex ? 'next' : 'pending';
  });

  // Where the wave is now: along the next hop, by elapsed fraction of its expected time.
  let wave: ChainAnalysis['wave'] = null;
  const next = lastIndex >= 0 ? hops[lastIndex] : undefined;
  if (next && next.fromCoords && next.toCoords && next.etaTs != null && lastArrival) {
    const expectedMs = next.etaTs - lastArrival.ts;
    const fraction = expectedMs > 0 ? (nowMs - lastArrival.ts) / expectedMs : 1;
    wave = { hop: next, position: interpolate(next.fromCoords, next.toCoords, fraction), fraction: Math.min(1, Math.max(0, fraction)) };
  }

  // THE THEORY: from the head's arrival, each hop takes its configured minutes
  // (the card's travel_minutes; failing that, its miles at the configured
  // speed). The wave's planned position now is the hop whose window holds
  // `nowMs`, interpolated; past the tail it sits on the tail.
  let theory: ChainTheory | null = null;
  const headArrival = arrivals[0];
  if (headArrival != null) {
    const planned: Array<number | null> = [headArrival];
    let clock: number | null = headArrival;
    for (const hop of hops) {
      const minutes =
        hop.configuredMinutes ??
        (hop.distanceMiles != null && configuredMph != null ? (hop.distanceMiles / configuredMph) * 60 : null);
      clock = clock != null && minutes != null ? clock + minutes * 60_000 : null;
      hop.theoryEtaTs = clock;
      planned.push(clock);
    }
    let theoryWave: ChainTheory['wave'] = null;
    let atTail = false;
    for (let i = 0; i < hops.length; i++) {
      const start = planned[i];
      const end = planned[i + 1];
      if (start == null || end == null) break;
      const last = i === hops.length - 1;
      if (nowMs <= end || last) {
        const fraction = end > start ? Math.min(1, Math.max(0, (nowMs - start) / (end - start))) : 1;
        const hop = hops[i];
        if (hop.fromCoords && hop.toCoords) {
          theoryWave = { hop, position: interpolate(hop.fromCoords, hop.toCoords, fraction), fraction };
        }
        atTail = last && nowMs >= end;
        break;
      }
    }
    const plannedAtLast = lastIndex >= 0 ? planned[lastIndex] : null;
    theory = {
      arrivals: planned,
      wave: theoryWave,
      atTail,
      lastArrivalVsPlanMin:
        lastArrival && plannedAtLast != null ? Math.round((lastArrival.ts - plannedAtLast) / 60_000) : null,
    };
  }

  return {
    chain,
    devices,
    hops,
    observedMph,
    hopsObserved: observed.length,
    trendMph,
    etaMph,
    etaBasis,
    lastArrival,
    wave,
    theory,
  };
}

/** Every chain among the given devices, analysed. */
export function analyzeChains(devices: ChainDevice[], nowMs: number): ChainAnalysis[] {
  return buildChains(devices).map((c) => analyzeChain(c.chain, c.devices, nowMs));
}

/**
 * What the map draws, with the hops above a FORK drawn once. Two branches
 * (Jellystone → Stonewall, Jellystone → Luckenbach) are two chains that share
 * every hop above Jellystone; without this the shared hops, the wave dot and
 * the theory ring would be stacked twice on the same spot. A shared hop keeps
 * the first chain's analysis and names the gauges those branches end at.
 */
export interface HopOnMap {
  analysis: ChainAnalysis;
  hop: ChainHop;
  /** The gauges the branches through here end at — both above a fork. */
  branches: string[];
}

export interface MarkerOnMap {
  analysis: ChainAnalysis;
  wave: NonNullable<ChainAnalysis['wave']>;
  /** The gauges the branches through here end at — both above a fork. */
  branches: string[];
}

const hopKey = (hop: ChainHop): string => `${hop.from.id}-${hop.to.id}`;

/** Lower = further along: passed first, then the soonest ETA. */
const STATUS_RANK: Record<HopStatus, number> = { passed: 0, next: 1, pending: 2, unknown: 3 };
const hopRank = (hop: ChainHop): number => STATUS_RANK[hop.status] * 1e15 + (hop.etaTs ?? Number.MAX_SAFE_INTEGER / 2);

export function hopsOnMap(analyses: ChainAnalysis[]): HopOnMap[] {
  const byKey = new Map<string, HopOnMap>();
  for (const analysis of analyses) {
    for (const hop of analysis.hops) {
      const seen = byKey.get(hopKey(hop));
      if (seen) {
        seen.branches.push(chainTailLabel(analysis));
        // Below a confluence the same hop belongs to several paths with their own
        // ETAs: keep the EARLIEST wave, as the simulator does.
        if (hopRank(hop) < hopRank(seen.hop)) {
          seen.hop = hop;
          seen.analysis = analysis;
        }
      } else byKey.set(hopKey(hop), { analysis, hop, branches: [chainTailLabel(analysis)] });
    }
  }
  return [...byKey.values()];
}

/** The wave markers to draw — `pick` chooses the observed wave or the theory's — one per hop. */
export function markersOnMap(
  analyses: ChainAnalysis[],
  pick: (analysis: ChainAnalysis) => ChainAnalysis['wave'] | undefined,
): MarkerOnMap[] {
  const byKey = new Map<string, MarkerOnMap>();
  for (const analysis of analyses) {
    const wave = pick(analysis);
    if (!wave) continue;
    const seen = byKey.get(hopKey(wave.hop));
    if (seen) {
      seen.branches.push(chainTailLabel(analysis));
      if (wave.fraction > seen.wave.fraction) {
        seen.wave = wave; // the wave that is further along — the earliest
        seen.analysis = analysis;
      }
    } else byKey.set(hopKey(wave.hop), { analysis, wave, branches: [chainTailLabel(analysis)] });
  }
  return [...byKey.values()];
}

export const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Map labels are to the minute; seconds are noise on a tooltip. */
const clock = (ts: number): string =>
  new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** One row of a map tooltip: a short label and its value. */
export interface TipRow {
  label: string;
  value: string;
}

const row = (label: string, value: string): TipRow => ({ label, value });

/** The gauges an operator knows by name, not the chain's generated id. */
export const chainHeadLabel = (analysis: ChainAnalysis): string =>
  deviceLabel(analysis.devices[0]);
export const chainTailLabel = (analysis: ChainAnalysis): string =>
  deviceLabel(analysis.devices[analysis.devices.length - 1]);

/** "Harper → Stillwater" — the heading over a hop's rows. */
export function hopTitle(hop: ChainHop): string {
  return `${deviceLabel(hop.from)} → ${deviceLabel(hop.to)}`;
}

/** Where the water is on this hop, in a word — the tooltip's chip. */
export function hopStatusLabel(hop: ChainHop): string {
  return { passed: 'Passed', next: 'Next', pending: 'Pending', unknown: 'Unknown' }[hop.status];
}

/**
 * The hop as label/value rows: how far, how fast, when the water gets there.
 * Rows, not sentences — the tooltip is a readout, not an explanation of the
 * arithmetic behind it (that lives on the waterway card in device settings).
 */
export function hopRows(hop: ChainHop): TipRow[] {
  const rows: TipRow[] = [];
  if (hop.distanceMiles != null) rows.push(row('Distance', `${round1(hop.distanceMiles)} mi`));
  if (hop.observedMph != null) rows.push(row('Speed', `${round1(hop.observedMph)} mph`));
  else if (hop.configuredMinutes != null) rows.push(row('Travel', `${Math.round(hop.configuredMinutes)} min`));
  if (hop.status === 'passed' && hop.toArrivalTs != null) rows.push(row('Arrived', clock(hop.toArrivalTs)));
  else if (hop.etaTs != null) rows.push(row('ETA', clock(hop.etaTs)));
  if (hop.theoryEtaTs != null) rows.push(row('Plan', clock(hop.theoryEtaTs)));
  return rows;
}

/** The wave: its speed, whether it is picking up, where it was last seen. */
export function waveRows(analysis: ChainAnalysis): TipRow[] {
  const rows: TipRow[] = [];
  if (analysis.observedMph != null) rows.push(row('Speed', `${round1(analysis.observedMph)} mph observed`));
  else if (analysis.etaMph != null) rows.push(row('Speed', `${round1(analysis.etaMph)} mph configured`));
  if (analysis.trendMph != null) {
    const t = round1(analysis.trendMph);
    rows.push(row('Trend', t > 0.05 ? `speeding up (+${t} mph)` : t < -0.05 ? `slowing (${t} mph)` : 'steady'));
  }
  if (analysis.lastArrival)
    rows.push(row('Last seen', `${deviceLabel(analysis.lastArrival.device)} ${clock(analysis.lastArrival.ts)}`));
  const next = analysis.wave?.hop;
  if (next?.etaTs != null) rows.push(row('Next', `${deviceLabel(next.to)} ${clock(next.etaTs)}`));
  return rows;
}

/** The plan — where the configured speeds put the wave now. */
export function theoryRows(analysis: ChainAnalysis): TipRow[] | null {
  const t = analysis.theory;
  if (!t) return null;
  const rows: TipRow[] = [];
  if (t.atTail && t.wave) rows.push(row('Now', `past ${deviceLabel(t.wave.hop.to)}`));
  else if (t.wave) {
    rows.push(row('Now', `${Math.round(t.wave.fraction * 100)}% to ${deviceLabel(t.wave.hop.to)}`));
    if (t.wave.hop.theoryEtaTs != null) rows.push(row('Due there', clock(t.wave.hop.theoryEtaTs)));
  } else rows.push(row('Now', 'no configured travel time'));
  if (analysis.lastArrival && t.lastArrivalVsPlanMin != null) {
    const m = t.lastArrivalVsPlanMin;
    rows.push(row('Water', m === 0 ? 'on plan' : `${Math.abs(m)} min ${m > 0 ? 'behind' : 'ahead'}`));
  }
  return rows;
}

/** The chain in one muted line, by its end gauges — never by its id. */
export function chainSummary(analysis: ChainAnalysis): string {
  const parts = [
    `${chainHeadLabel(analysis)} → ${chainTailLabel(analysis)}`,
    `${analysis.devices.length} gauges`,
  ];
  if (analysis.observedMph != null) parts.push(`${round1(analysis.observedMph)} mph observed`);
  else if (analysis.etaBasis === 'configured' && analysis.etaMph != null)
    parts.push(`${round1(analysis.etaMph)} mph configured`);
  else parts.push('no arrivals yet');
  return parts.join(' · ');
}

/**
 * Above a fork the same hop belongs to every branch below it. Say so by the
 * gauges those branches end at — "Also feeds Lady Bird" — since the chain ids
 * ("chain-1112") mean nothing to the operator reading the map.
 */
export function sharedBranchLabel(branches: string[], self: string): string | null {
  const others = [...new Set(branches)].filter((b) => b !== self);
  return others.length === 0 ? null : `Also feeds ${others.join(', ')}`;
}

